import { url } from '../api/endpointURL';

class LprJourneyAPI {

  /**
   * Fetch all networks for a given business name.
   * @param {string} businessName - the business name (e.g. "HIVE")
   * @returns {Promise<Array>} networks
   */
  async getNetworksByBusinessName(businessName) {
    try {
      const response = await fetch(
        `${url}/lpr-journey/networks?businessName=${encodeURIComponent(businessName)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to fetch networks');
    } catch (error) {
      console.error('Error fetching networks:', error);
      throw error;
    }
  }

  /**
   * Fetch all cameras for a given network.
   * @param {string} networkId
   * @returns {Promise<Array>} cameras
   */
  async getCamerasByNetworkId(networkId) {
    try {
      const response = await fetch(
        `${url}/lpr-journey/cameras?networkId=${encodeURIComponent(networkId)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to fetch cameras');
    } catch (error) {
      console.error('Error fetching cameras:', error);
      throw error;
    }
  }

  /**
   * Get the saved camera coordinate config for a network.
   * @param {string} networkId
   * @returns {Promise<{ cameras, hasBackgroundImage, isFullyConfigured }>}
   */
  async getConfig(networkId) {
    try {
      const response = await fetch(
        `${url}/lpr-journey/config?networkId=${encodeURIComponent(networkId)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to fetch config');
    } catch (error) {
      console.error('Error fetching LPR Journey config:', error);
      throw error;
    }
  }

  /**
   * Save camera coordinates for a network.
   * @param {string} networkId
   * @param {Array} cameraPositions - [{ camera_id, hwid, camera_name, pos_x, pos_y }]
   * @param {boolean} hasBackgroundImage
   */
  async saveConfig(networkId, cameraPositions, hasBackgroundImage = false) {
    try {
      const response = await fetch(`${url}/lpr-journey/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ networkId, cameraPositions, hasBackgroundImage })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to save config');
    } catch (error) {
      console.error('Error saving LPR Journey config:', error);
      throw error;
    }
  }

  /**
   * Update only the background image flag for a network.
   * @param {string} networkId
   * @param {boolean} hasBackgroundImage
   */
  async updateBackgroundImageFlag(networkId, hasBackgroundImage) {
    try {
      const response = await fetch(`${url}/lpr-journey/config/background`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ networkId, hasBackgroundImage })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to update background image flag');
    } catch (error) {
      console.error('Error updating background image flag:', error);
      throw error;
    }
  }

  /**
   * Delete all config for a network (reset).
   * @param {string} networkId
   */
  async deleteConfig(networkId) {
    try {
      const response = await fetch(
        `${url}/lpr-journey/config?networkId=${encodeURIComponent(networkId)}`,
        { method: 'DELETE', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to delete config');
    } catch (error) {
      console.error('Error deleting LPR Journey config:', error);
      throw error;
    }
  }

  /**
   * Resolve the IANA timezone for a network.
   * @param {string} networkId
   * @returns {Promise<string>} IANA timezone string (e.g. 'Australia/Sydney'), falls back to 'UTC'
   */
  async getNetworkTimezone(networkId) {
    try {
      const response = await fetch(
        `${url}/lpr-journey/timezone?networkId=${encodeURIComponent(networkId)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data.timezone;
      throw new Error(result.error || 'Failed to fetch network timezone');
    } catch (error) {
      console.error('Error fetching network timezone:', error);
      return 'UTC';
    }
  }

  /**
   * Fetch the historical (rolling-window) average duration per camera-pair.
   *
   * The benchmark window is always the last 30 full calendar days before today
   * (server local timezone), independent of any user-selected date:
   *   endDate   = yesterday  (inclusive)
   *   startDate = endDate − 29 days  (inclusive)
   *
   * @param {string} networkId
   * @param {number} [windowDays] - rolling window in days (default: server-side 30)
   * @returns {Promise<Array>} [{ from_camera_id, to_camera_id,
   *                               history_avg_duration_seconds,
   *                               history_min_duration_seconds,
   *                               history_max_duration_seconds,
   *                               history_days_active }]
   */
  async getHistoricalTransitions(networkId, windowDays) {
    try {
      const params = new URLSearchParams({ networkId });
      if (windowDays !== undefined) params.set('windowDays', String(windowDays));
      const response = await fetch(
        `${url}/lpr-journey/transitions/history?${params.toString()}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to fetch historical transitions');
    } catch (error) {
      console.error('Error fetching historical transitions:', error);
      throw error;
    }
  }

  /**
   * Fetch camera transition statistics for a network within a date range.
   * @param {string} networkId
   * @param {string} startDate - YYYY-MM-DD
   * @param {string} endDate   - YYYY-MM-DD
   * @returns {Promise<Array>} transitions
   */
  async getCameraTransitions(networkId, startDate, endDate) {
    try {
      const params = new URLSearchParams({ networkId, startDate, endDate });
      const response = await fetch(
        `${url}/lpr-journey/transitions?${params.toString()}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to fetch camera transitions');
    } catch (error) {
      console.error('Error fetching camera transitions:', error);
      throw error;
    }
  }
  /**
   * Fetch all journeys for a network within a date range.
   * @param {string} networkId
   * @param {string} startDate - YYYY-MM-DD
   * @param {string} endDate   - YYYY-MM-DD
   * @returns {Promise<Array>} journeys
   */
  async getVehicleRoutes(networkId, startDate, endDate) {
    try {
      const params = new URLSearchParams({ networkId, startDate, endDate });
      const response = await fetch(
        `${url}/lpr-journey/vehicle-routes?${params.toString()}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to fetch vehicle routes');
    } catch (error) {
      console.error('Error fetching vehicle routes:', error);
      throw error;
    }
  }

  /**
   * Fetch detection events for a single journey.
   * @param {string} journeyId
   * @returns {Promise<Array>} detections ordered by detected_at
   */
  async getJourneyDetections(journeyId) {
    try {
      const params = new URLSearchParams({ journeyId });
      const response = await fetch(
        `${url}/lpr-journey/vehicle-routes/detections?${params.toString()}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to fetch journey detections');
    } catch (error) {
      console.error('Error fetching journey detections:', error);
      throw error;
    }
  }

  /**
   * Fetch detection events for multiple journeys in one request.
   * @param {string[]} journeyIds
   * @returns {Promise<Array>} detections for all journeys
   */
  async getDetectionsForJourneys(journeyIds) {
    try {
      const response = await fetch(`${url}/lpr-journey/vehicle-routes/detections/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ journeyIds }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to fetch bulk journey detections');
    } catch (error) {
      console.error('Error fetching bulk journey detections:', error);
      throw error;
    }
  }
}

export default new LprJourneyAPI();
